import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { IGasSupply, defaultValue } from 'app/shared/model/gas-supply.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { DEFAULT_ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import axios from 'axios';

const initialState: EntityState<IGasSupply> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

interface IGasSupplyScheduling extends IQueryParams {
  id?: number | string;
  schedulingId?: number | string;
}

const apiUrl = 'api/gas-supplies';

// Actions

export const getEntities = createAsyncThunk('gasSupply/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IGasSupply[]>(requestUrl);
});

export const getEntitiesByScheduling = createAsyncThunk(
  'gasSupply/fetch_entity_list_scheduling',
  async ({ page, size, sort, schedulingId }: IGasSupplyScheduling) => {
    const schedulingIdRequest = schedulingId;
    const requestUrl = `${apiUrl}/scheduling/${schedulingIdRequest}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IGasSupply[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'gasSupply/fetch_entity',
  async (id: string) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IGasSupply>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'gasSupply/create_entity',
  async (entity: IGasSupplyScheduling, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.schedulingId}`;
    const result = await axios.post<IGasSupply>(requestUrl, cleanEntity(entity));

    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'gasSupply/update_entity',
  async (entity: IGasSupply, thunkAPI) => {
    const result = await axios.put<IGasSupply>(`${apiUrl}/${entity.id}`, cleanEntity(entity));

    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'gasSupply/partial_update_entity',
  async (entity: IGasSupply, thunkAPI) => {
    const result = await axios.patch<IGasSupply>(`${apiUrl}/${entity.id}`, cleanEntity(entity));

    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'gasSupply/delete_entity',
  async ({ id, schedulingId }: { id: string | number; schedulingId: string | number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IGasSupply>(requestUrl);
    thunkAPI.dispatch(
      getEntitiesByScheduling({
        page: 0,
        size: DEFAULT_ITEMS_PER_PAGE,
        sort: 'id,ASC',
        schedulingId,
      })
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const GasSupplySlice = createEntitySlice({
  name: 'gasSupply',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesByScheduling), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesByScheduling), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
        state.loading = true;
      });
  },
});

export const { reset } = GasSupplySlice.actions;

// Reducer
export default GasSupplySlice.reducer;
